import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createCart,
  addToCart,
  updateCartItem,
  removeFromCart,
} from '../services/shopify';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);

  // Initialize or retrieve cart from localStorage
  useEffect(() => {
    const initCart = async () => {
      const cartId = localStorage.getItem('shopify_cart_id');

      if (!cartId) {
        try {
          const newCart = await createCart();
          setCart(newCart);
          localStorage.setItem('shopify_cart_id', newCart.id);
        } catch (error) {
          console.error('Error creating cart:', error);
        }
      } else {
        // In a production app, you'd want to fetch the existing cart
        // For now, we'll create a new one if needed
        setCart({ id: cartId, lines: { edges: [] } });
      }
    };

    initCart();
  }, []);

  const addItem = async (merchandiseId, quantity = 1) => {
    setLoading(true);
    try {
      let currentCart = cart;

      if (!currentCart) {
        currentCart = await createCart();
        localStorage.setItem('shopify_cart_id', currentCart.id);
      }

      const lines = [{ merchandiseId, quantity }];
      const updatedCart = await addToCart(currentCart.id, lines);
      setCart(updatedCart);
      return updatedCart;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (lineId, quantity) => {
    setLoading(true);
    try {
      const lines = [{ id: lineId, quantity }];
      const updatedCart = await updateCartItem(cart.id, lines);
      setCart(updatedCart);
      return updatedCart;
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (lineId) => {
    setLoading(true);
    try {
      const updatedCart = await removeFromCart(cart.id, [lineId]);
      setCart(updatedCart);
      return updatedCart;
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getCartCount = () => {
    if (!cart || !cart.lines) return 0;
    return cart.lines.edges.reduce(
      (total, item) => total + item.node.quantity,
      0
    );
  };

  const value = {
    checkout: cart, // Keep as checkout for backwards compatibility with Cart component
    loading,
    addItem,
    updateItem,
    removeItem,
    cartCount: getCartCount(),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
